import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Box, Package, AlertTriangle, Plus, Building2
} from "lucide-react";

export default function AdminInventoryTab({ 
  products, 
  lowStockProducts, 
  kitchenInventoryData,
  onOpenStockDialog
}) {
  const [selectedInventoryView, setSelectedInventoryView] = useState("all");

  const totalStock = products.reduce((sum, p) => sum + (p.stock_quantity || 0), 0);

  return (
    <>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-6 mb-6">
        <Card className="border-0 shadow-card">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm text-muted-foreground">Total Stock</p>
                <p className="text-xl lg:text-3xl font-heading mt-1">{totalStock}</p>
              </div>
              <div className="w-8 h-8 lg:w-12 lg:h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                <Box className="w-4 h-4 lg:w-6 lg:h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm text-muted-foreground">Low Stock</p>
                <p className="text-xl lg:text-3xl font-heading mt-1 text-yellow-600">{lowStockProducts.length}</p>
              </div>
              <div className="w-8 h-8 lg:w-12 lg:h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 lg:w-6 lg:h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm text-muted-foreground">Out of Stock</p>
                <p className="text-xl lg:text-3xl font-heading mt-1 text-red-600">
                  {products.filter(p => !p.in_stock).length}
                </p>
              </div>
              <div className="w-8 h-8 lg:w-12 lg:h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <Package className="w-4 h-4 lg:w-6 lg:h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm text-muted-foreground">Categories</p>
                <p className="text-xl lg:text-3xl font-heading mt-1">{new Set(products.map(p => p.category)).size}</p>
              </div>
              <div className="w-8 h-8 lg:w-12 lg:h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Package className="w-4 h-4 lg:w-6 lg:h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alert List */}
      <Card className="border-0 shadow-card" data-testid="low-stock-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg lg:text-xl flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            Low Stock Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {lowStockProducts.length === 0 ? (
            <div className="text-center py-8 text-green-600">
              <Box className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>All products are well stocked!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {lowStockProducts.map((p) => (
                <div 
                  key={p.product_id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 lg:p-4 bg-yellow-50 border border-yellow-200 rounded-xl gap-3"
                >
                  <div className="flex items-center gap-3 lg:gap-4">
                    {p.image_url && (
                      <img src={p.image_url} alt={p.name} className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg object-cover" />
                    )}
                    <div>
                      <p className="font-semibold text-sm lg:text-base">{p.name}</p>
                      <p className="text-xs lg:text-sm text-muted-foreground">SKU: {p.sku || "-"}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-3 lg:gap-4">
                    <div className="text-left sm:text-right">
                      <p className="text-xl lg:text-2xl font-heading text-yellow-600">{p.stock_quantity || 0}</p>
                      <p className="text-xs text-muted-foreground">Min: {p.min_stock_alert || 10}</p>
                    </div>
                    <Button 
                      size="sm" 
                      className="rounded-full"
                      onClick={() => onOpenStockDialog(p)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Stock
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Kitchen Inventory Overview */}
      <Card className="border-0 shadow-card mt-6" data-testid="kitchen-inventory-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg lg:text-xl flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Kitchen Inventory
          </CardTitle>
          <Select value={selectedInventoryView} onValueChange={setSelectedInventoryView}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by kitchen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Kitchens</SelectItem>
              {kitchenInventoryData.kitchens.map((k) => (
                <SelectItem key={k.kitchen_id} value={k.kitchen_id}>{k.kitchen_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {kitchenInventoryData.kitchens.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No kitchens configured yet</p>
              <p className="text-sm mt-2">Go to Kitchens & Outlets to add kitchens and their inventory</p>
            </div>
          ) : (
            <div className="space-y-6">
              {kitchenInventoryData.kitchens
                .filter(k => selectedInventoryView === "all" || k.kitchen_id === selectedInventoryView)
                .map((kitchen) => (
                <div key={kitchen.kitchen_id} className="border rounded-xl p-4 bg-muted/30">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-lg flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-primary" />
                      {kitchen.kitchen_name}
                    </h4>
                    <Badge variant="outline" className="text-xs">
                      {kitchen.inventory.length} items
                    </Badge>
                  </div>
                  {kitchen.inventory.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No inventory items in this kitchen</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {kitchen.inventory.map((item) => (
                        <div 
                          key={item.inventory_id} 
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            item.quantity <= 0 ? 'bg-red-50 border-red-200' : 
                            item.quantity <= 10 ? 'bg-yellow-50 border-yellow-200' : 
                            'bg-white'
                          }`}
                        >
                          <div>
                            <p className="font-medium text-sm">{item.product_name}</p>
                            <p className="text-xs text-muted-foreground">{item.unit}</p>
                          </div>
                          <div className={`text-lg font-bold ${
                            item.quantity <= 0 ? 'text-red-600' : 
                            item.quantity <= 10 ? 'text-yellow-600' : 
                            'text-green-600'
                          }`}>
                            {item.quantity}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
